import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { ChangeSummary, DefinitionList, SectionCard, FlowSteps, FormDialog, FormTextField, LoadingIndicator } from '../../../design-system';
import type { PersonNameProfile, PersonNameValue, UpdatePersonNamesInput } from './model';
import type { PersonNamePort } from './port';

export type PersonNameDraft = {
  legalFamilyName: string;
  legalGivenName: string;
  legalMiddleName: string;
  legalEnglishFamilyName: string;
  legalEnglishGivenName: string;
  legalEnglishMiddleName: string;
  preferredName: string;
  preferredEnglishName: string;
  reason: string;
};

type PersonNameChangeDialogProps = {
  open: boolean;
  port: PersonNamePort;
  personId: string;
  workerName: string;
  workerNumber: string;
  fixtureMode: boolean;
  fixtureProfile?: PersonNameProfile;
  onClose: () => void;
  onSaved: (profile: PersonNameProfile) => void;
};

const emptyDraft: PersonNameDraft = {
  legalFamilyName: '',
  legalGivenName: '',
  legalMiddleName: '',
  legalEnglishFamilyName: '',
  legalEnglishGivenName: '',
  legalEnglishMiddleName: '',
  preferredName: '',
  preferredEnglishName: '',
  reason: '',
};

const directReasonOption = 'DIRECT_INPUT';
const nameChangeReasonOptions = [
  '법적 이름 변경',
  '이름 표기 정정',
  '선호 이름 변경',
  '영문 이름 변경',
] as const;

type DialogStep = 'edit' | 'review';

type NameFieldChange = {
  key: string;
  label: string;
  before: string;
  after: string;
};

const nameSectionTitleSx = { color: 'text.primary', fontWeight: 750 } as const;

function fixtureNameProfile(personId: string, workerName: string): PersonNameProfile {
  const characters = Array.from(workerName.trim());
  const lastName = characters[0] ?? '';
  const firstName = characters.slice(1).join('');
  return {
    person: { id: personId, descriptor: workerName },
    legalName: { fullName: workerName, firstName, lastName, countryCode: 'KR', locale: 'ko-KR' },
    preferredName: { fullName: workerName, countryCode: 'KR', locale: 'ko-KR' },
    version: 2,
  };
}

export function personNameDraftFromProfile(profile: PersonNameProfile): PersonNameDraft {
  return {
    legalFamilyName: profile.legalName.lastName ?? '',
    legalGivenName: profile.legalName.firstName ?? '',
    legalMiddleName: profile.legalName.middleName ?? '',
    legalEnglishFamilyName: profile.legalEnglishName?.lastName ?? '',
    legalEnglishGivenName: profile.legalEnglishName?.firstName ?? '',
    legalEnglishMiddleName: profile.legalEnglishName?.middleName ?? '',
    preferredName: profile.preferredName?.fullName ?? '',
    preferredEnglishName: profile.preferredEnglishName?.fullName ?? '',
    reason: '',
  };
}

function optionalName(fullName: string, countryCode: string, locale: string,
  current?: PersonNameValue): PersonNameValue | undefined {
  const normalized = fullName.trim();
  if (!normalized) return undefined;
  if (current?.fullName.trim() === normalized) {
    return { ...current, fullName: normalized, countryCode: current.countryCode || countryCode, locale: current.locale || locale };
  }
  return { fullName: normalized, countryCode, locale };
}

function nameDefaults(current: PersonNameValue | undefined, fallbackCountry: string,
  fallbackLocale: string, localeFollowsCountry = false) {
  const countryCode = current?.countryCode?.trim() || fallbackCountry;
  const locale = current?.locale?.trim() || (localeFollowsCountry
    ? (countryCode.toUpperCase() === 'KR' ? 'ko-KR' : 'en-US')
    : fallbackLocale);
  return { countryCode, locale };
}

function structuredFullName(current: PersonNameValue, firstName: string, middleName: string,
  lastName: string, fallbackLocale: string): string {
  if ((current.firstName ?? '').trim() === firstName && (current.middleName ?? '').trim() === middleName &&
    (current.lastName ?? '').trim() === lastName) return current.fullName.trim();
  if ((current.locale || fallbackLocale).toLowerCase().startsWith('ko')) return `${lastName}${firstName}${middleName}`;
  return [firstName, middleName, lastName].filter(Boolean).join(' ');
}

export function buildPersonNameUpdateInput(profile: PersonNameProfile,
  draft: PersonNameDraft): UpdatePersonNamesInput {
  const legalFamilyName = draft.legalFamilyName.trim();
  const legalGivenName = draft.legalGivenName.trim();
  const legalMiddleName = draft.legalMiddleName.trim();
  const englishFamilyName = draft.legalEnglishFamilyName.trim();
  const englishGivenName = draft.legalEnglishGivenName.trim();
  const englishMiddleName = draft.legalEnglishMiddleName.trim();
  const legalDefaults = nameDefaults(profile.legalName, 'KR', 'ko-KR', true);
  const legalEnglishDefaults = nameDefaults(profile.legalEnglishName, 'US', 'en-US');
  const preferredDefaults = nameDefaults(profile.preferredName, 'KR', 'ko-KR');
  const preferredEnglishDefaults = nameDefaults(profile.preferredEnglishName, 'US', 'en-US');
  return {
    legalName: {
      fullName: structuredFullName(profile.legalName, legalGivenName, legalMiddleName, legalFamilyName, legalDefaults.locale),
      firstName: legalGivenName,
      middleName: legalMiddleName,
      lastName: legalFamilyName,
      ...legalDefaults,
    },
    legalEnglishName: englishFamilyName && englishGivenName ? {
      fullName: structuredFullName(profile.legalEnglishName ?? { fullName: '', locale: 'en-US' },
        englishGivenName, englishMiddleName, englishFamilyName, legalEnglishDefaults.locale),
      firstName: englishGivenName,
      middleName: englishMiddleName,
      lastName: englishFamilyName,
      ...legalEnglishDefaults,
    } : undefined,
    preferredName: optionalName(draft.preferredName, preferredDefaults.countryCode,
      preferredDefaults.locale, profile.preferredName),
    preferredEnglishName: optionalName(draft.preferredEnglishName, preferredEnglishDefaults.countryCode,
      preferredEnglishDefaults.locale, profile.preferredEnglishName),
    reason: draft.reason.trim(),
    version: profile.version,
  };
}

function comparableNames(input: UpdatePersonNamesInput) {
  return {
    legalName: input.legalName,
    legalEnglishName: input.legalEnglishName,
    preferredName: input.preferredName,
    preferredEnglishName: input.preferredEnglishName,
  };
}

function currentComparableNames(profile: PersonNameProfile) {
  return comparableNames(buildPersonNameUpdateInput(profile, personNameDraftFromProfile(profile)));
}

function displayName(value?: PersonNameValue): string {
  return value?.fullName.trim() || '등록 안 함';
}

function displayNamePart(value?: string): string {
  return value?.trim() || '등록 안 함';
}

function personNameFieldChanges(profile: PersonNameProfile,
  next: UpdatePersonNamesInput): NameFieldChange[] {
  return [
    { key: 'legalFamilyName', label: '법적 이름 · 성', before: displayNamePart(profile.legalName.lastName), after: displayNamePart(next.legalName.lastName) },
    { key: 'legalGivenName', label: '법적 이름 · 이름', before: displayNamePart(profile.legalName.firstName), after: displayNamePart(next.legalName.firstName) },
    { key: 'legalEnglishFamilyName', label: '영문 법적 이름 · 성', before: displayNamePart(profile.legalEnglishName?.lastName), after: displayNamePart(next.legalEnglishName?.lastName) },
    { key: 'legalEnglishGivenName', label: '영문 법적 이름 · 이름', before: displayNamePart(profile.legalEnglishName?.firstName), after: displayNamePart(next.legalEnglishName?.firstName) },
    { key: 'preferredName', label: '선호 이름', before: displayName(profile.preferredName), after: displayName(next.preferredName) },
    { key: 'preferredEnglishName', label: '영문 선호 이름', before: displayName(profile.preferredEnglishName), after: displayName(next.preferredEnglishName) },
  ].filter((change) => change.before !== change.after);
}

export function validatePersonNameChanges(profile: PersonNameProfile,
  draft: PersonNameDraft): string | undefined {
  const legalFamilyName = draft.legalFamilyName.trim();
  const legalGivenName = draft.legalGivenName.trim();
  const legalMiddleName = draft.legalMiddleName.trim();
  const englishFamilyName = draft.legalEnglishFamilyName.trim();
  const englishGivenName = draft.legalEnglishGivenName.trim();
  const englishMiddleName = draft.legalEnglishMiddleName.trim();
  if (!legalFamilyName || !legalGivenName) return '법적 이름의 성과 이름을 모두 입력해 주세요.';
  if ((englishFamilyName || englishGivenName || englishMiddleName) && (!englishFamilyName || !englishGivenName)) {
    return '영문 법적 이름은 성과 이름을 함께 입력하거나 모두 비워 주세요.';
  }
  for (const value of [legalFamilyName, legalGivenName, legalMiddleName, englishFamilyName, englishGivenName, englishMiddleName]) {
    if (Array.from(value).length > 100) return '이름의 각 항목은 100자 이내로 입력해 주세요.';
  }
  for (const value of [draft.preferredName.trim(), draft.preferredEnglishName.trim()]) {
    if (Array.from(value).length > 200) return '선호 이름은 200자 이내로 입력해 주세요.';
  }
  const next = buildPersonNameUpdateInput(profile, draft);
  if ([next.legalName.fullName, next.legalEnglishName?.fullName ?? ''].some((value) => Array.from(value).length > 200)) {
    return '조합된 법적 이름은 200자 이내로 입력해 주세요.';
  }
  if (JSON.stringify(comparableNames(next)) === JSON.stringify(currentComparableNames(profile))) {
    return '현재 이름과 다른 값을 한 개 이상 입력해 주세요.';
  }
  return undefined;
}

export function validatePersonNameDraft(profile: PersonNameProfile, draft: PersonNameDraft): string | undefined {
  const changesError = validatePersonNameChanges(profile, draft);
  if (changesError) return changesError;
  if (!draft.reason.trim()) return '변경 사유를 입력해 주세요.';
  if (Array.from(draft.reason.trim()).length > 500) return '변경 사유는 500자 이내로 입력해 주세요.';
  return undefined;
}

export function PersonNameChangeDialog({
  open,
  port,
  personId,
  workerName,
  fixtureMode,
  fixtureProfile,
  onClose,
  onSaved,
}: PersonNameChangeDialogProps) {
  const [profile, setProfile] = useState<PersonNameProfile>();
  const [draft, setDraft] = useState<PersonNameDraft>(emptyDraft);
  const [step, setStep] = useState<DialogStep>('edit');
  const [reasonOption, setReasonOption] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (!open || !personId) return undefined;
    let active = true;
    setLoading(true);
    setError(undefined);
    setProfile(undefined);
    setDraft(emptyDraft);
    setStep('edit');
    setReasonOption('');
    setIdempotencyKey(crypto.randomUUID());
    const request = fixtureMode
      ? Promise.resolve(fixtureProfile ?? fixtureNameProfile(personId, workerName))
      : port.getPersonNames(personId);
    void request.then((value) => {
      if (!active) return;
      setProfile(value);
      setDraft(personNameDraftFromProfile(value));
    }, (failure: unknown) => {
      if (!active) return;
      setError(failure instanceof Error ? failure.message : '현재 이름을 불러오지 못했습니다.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [fixtureMode, fixtureProfile, open, personId, port, workerName]);

  const nextInput = useMemo(() => profile ? buildPersonNameUpdateInput(profile, draft) : undefined, [draft, profile]);
  const fieldChanges = useMemo(() => profile && nextInput
    ? personNameFieldChanges(profile, nextInput)
    : [], [nextInput, profile]);
  const updateDraft = (field: keyof PersonNameDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setError(undefined);
  };
  const selectReason = (value: string) => {
    setReasonOption(value);
    updateDraft('reason', value === directReasonOption ? '' : value);
  };
  const closeOrBack = () => {
    if (step === 'review') {
      setStep('edit');
      setError(undefined);
      return;
    }
    onClose();
  };
  const submit = () => {
    if (!profile || !nextInput || saving) return;
    if (step === 'edit') {
      const validationError = validatePersonNameChanges(profile, draft);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(undefined);
      setStep('review');
      return;
    }
    if (!reasonOption) {
      setError('변경 사유를 선택해 주세요.');
      return;
    }
    const validationError = validatePersonNameDraft(profile, draft);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (fixtureMode) {
      onSaved({ person: profile.person, ...comparableNames(nextInput), version: profile.version + 1 });
      return;
    }
    setSaving(true);
    setError(undefined);
    void port.updatePersonNames(personId, nextInput, idempotencyKey).then(onSaved, (failure: unknown) => {
      if (typeof failure === 'object' && failure !== null && 'code' in failure && failure.code === 'VERSION_CONFLICT') {
        setError('다른 관리자가 먼저 이름을 변경했습니다. 창을 닫고 최신 이름을 다시 확인해 주세요.');
        return;
      }
      setError(failure instanceof Error ? failure.message : '이름을 변경하지 못했습니다.');
    }).finally(() => setSaving(false));
  };

  return (
    <FormDialog
      open={open}
      onClose={closeOrBack}
      onSubmit={submit}
      title="이름 변경"
      headerContent={<FlowSteps activeStep={step === 'edit' ? 0 : 1} ariaLabel="이름 변경 단계" steps={['입력', '검토·반영']} variant="compact" testId="person-name-change-stepper" />}
      cancelLabel={step === 'edit' ? '취소' : '돌아가기'}
      submitLabel={step === 'edit' ? '검토 하기' : '반영 하기'}
      busy={saving}
      busyLabel="반영 중"
      submitDisabled={!profile || loading}
    >
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <Stack sx={{ alignItems: 'center', py: 2 }}><LoadingIndicator label="현재 이름을 불러오는 중입니다." /></Stack>}
      {profile && step === 'edit' && <>
        <Typography component="h3" variant="subtitle2" sx={nameSectionTitleSx}>법적 이름 (한글)</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <FormTextField required label="성" value={draft.legalFamilyName} autoFocus placeholder="예: 홍" slotProps={{ htmlInput: { maxLength: 100, 'aria-label': '법적 이름 · 성' } }} onChange={(event) => updateDraft('legalFamilyName', event.target.value)} />
          <FormTextField required label="이름" value={draft.legalGivenName} placeholder="예: 길동" slotProps={{ htmlInput: { maxLength: 100, 'aria-label': '법적 이름 · 이름' } }} onChange={(event) => updateDraft('legalGivenName', event.target.value)} />
        </Stack>
        <Typography component="h3" variant="subtitle2" sx={nameSectionTitleSx}>법적 이름 (영문)</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <FormTextField label="성" value={draft.legalEnglishFamilyName} placeholder="예: Hong" slotProps={{ htmlInput: { maxLength: 100, 'aria-label': '영문 법적 이름 · 성' } }} onChange={(event) => updateDraft('legalEnglishFamilyName', event.target.value)} />
          <FormTextField label="이름" value={draft.legalEnglishGivenName} placeholder="예: Gildong" slotProps={{ htmlInput: { maxLength: 100, 'aria-label': '영문 법적 이름 · 이름' } }} onChange={(event) => updateDraft('legalEnglishGivenName', event.target.value)} />
        </Stack>
        <Typography component="h3" variant="subtitle2" sx={nameSectionTitleSx}>선호 이름</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <FormTextField label="한글" value={draft.preferredName} placeholder="예: 로빈" slotProps={{ htmlInput: { maxLength: 200, 'aria-label': '선호 이름' } }} onChange={(event) => updateDraft('preferredName', event.target.value)} />
          <FormTextField label="영문" value={draft.preferredEnglishName} placeholder="예: Robin" slotProps={{ htmlInput: { maxLength: 200, 'aria-label': '영문 선호 이름' } }} onChange={(event) => updateDraft('preferredEnglishName', event.target.value)} />
        </Stack>
      </>}
      {profile && step === 'review' && <>
        <SectionCard title="기본 정보" showDivider={false}>
          <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
            <DefinitionList items={[[ '대상 구성원', workerName ]]} />
        <FormTextField
          required
          select
          autoFocus
          label="변경 사유"
          value={reasonOption}
          onChange={(event) => selectReason(event.target.value)}
        >
          <MenuItem value="">선택해 주세요</MenuItem>
          {nameChangeReasonOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          <MenuItem value={directReasonOption}>직접 입력</MenuItem>
        </FormTextField>
        {reasonOption === directReasonOption && <FormTextField
          required
          label="변경 사유 직접 입력"
          value={draft.reason}
          characterCount={{ current: Array.from(draft.reason.trim()).length, max: 500 }}
          multiline
          minRows={3}
          slotProps={{ htmlInput: { maxLength: 500 } }}
          onChange={(event) => updateDraft('reason', event.target.value)}
        />}
          </Stack>
        </SectionCard>
        <SectionCard title="변경 내용" showDivider={false}>
          <Box component="section" aria-label="이름 변경 내용" data-testid="person-name-change-review" sx={{ px: 2, pb: 2 }}>
            {fieldChanges.length > 0 ? <ChangeSummary layout="inline" emptyValue="없음" items={fieldChanges.map((change) => ({
              id: change.key, label: change.label, before: change.before, after: change.after,
            }))} /> : null}
          </Box>
        </SectionCard>
      </>}
    </FormDialog>
  );
}
